import {
  conflict,
  deepEquals,
  getQueryString,
  getReferenceString,
  type MedplumClient,
  OperationOutcomeError,
  type PatchOperation,
  preconditionFailed,
  type QueryTypes,
  type ResourceArray,
  type SubscriptionEventMap,
} from "@medplum/core";
import type {
  Bundle,
  CodeableConcept,
  Coding,
  ContactPoint,
  Extension,
  Identifier,
  Meta,
  Quantity,
  Reference,
  Resource,
  ResourceType,
} from "@medplum/fhirtypes";
import { z } from "zod";
import type {
  NullableProps,
  ResourceData,
  ResourceRef,
  ValueOf,
  WithId,
} from "./fhir-resource-types";
import {
  assertValidAccountCompartments,
  cleanObject,
  findIdentifier,
  getPracticeOrganizationFromMeta,
  isPlainObject,
  type MedplumMeta,
  type ResourceWithIdentifierAndExtension,
  upsertReferenceByReference,
  weakETag,
} from "./fhir-resource-utils";
import { reportAccountCompartmentFallback, reportFHIRWriteFailureIfApplicable } from "./telemetry";

const EXTENSION_ARRAY_KEYS = new Set(["extension", "modifierExtension"]);

/**
 * Attempts `upsertResource` makes when a patch loses a version race. A repeat collision needs a
 * third writer to commit inside one read-write pair, so the odds fall off fast; background callers
 * retry the whole step behind this.
 */
const UPSERT_PATCH_MAX_ATTEMPTS = 3;

function sanitizeForCreateValue(
  value: unknown,
  options: { key?: string; path?: string } = {},
): unknown {
  const { key } = options;
  const path = options.path ?? "$";

  if (value === undefined) return undefined;
  if (value === null) {
    throw new Error(`Invalid create payload: null at ${path}`);
  }

  if (Array.isArray(value)) {
    const cleanedItems = value
      .map((item, index) => sanitizeForCreateValue(item, { key, path: `${path}[${index}]` }))
      .filter((item): item is Exclude<typeof item, undefined> => item !== undefined);
    return cleanedItems.length > 0 ? cleanedItems : undefined;
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const cleaned: Record<string, unknown> = {};
  for (const [childKey, childValue] of Object.entries(value)) {
    const sanitizedChild = sanitizeForCreateValue(childValue, {
      key: childKey,
      path: `${path}.${childKey}`,
    });
    if (sanitizedChild !== undefined) {
      cleaned[childKey] = sanitizedChild;
    }
  }

  if (key && EXTENSION_ARRAY_KEYS.has(key)) {
    const meaningfulKeys = Object.keys(cleaned).filter(
      (childKey) => childKey !== "url" && childKey !== "id",
    );
    if (meaningfulKeys.length === 0) {
      return undefined;
    }
  }

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

function createResourceArray<TResource extends Resource>(
  resources: ReadonlyArray<TResource> = [],
): ResourceArray<TResource> {
  return Object.assign([...resources], {
    bundle: {
      resourceType: "Bundle" as const,
      type: "searchset" as const,
      total: resources.length,
      entry: resources.map((resource) => ({ resource })),
    },
  });
}

type FhirResourceConstructor<R extends Resource = Resource> = abstract new (
  ...args: any[]
) => FhirResource<R>;

interface StaticProps<_Model extends FhirResourceConstructor = FhirResourceConstructor> {
  ResourceType: ResourceType;
  /**
   * When set on a subclass, failed writes through `FhirResource` helpers invoke the reporter
   * registered with `registerFhirWriteFailureReporter`
   */
  isSyncFrom?: string[] | undefined;
  /**
   * Reference-array fields that hold a set rather than a value, such as `derivedFrom`. A patch that
   * asks for the union (`PatchResourceOptions.unionReferenceFields`) adds the stored row's entries
   * to these fields instead of replacing them, so a concurrent writer's references survive. Unset
   * on a subclass means every field keeps last-write-wins.
   */
  UnionedReferenceFields?: readonly string[] | undefined;
  /**
   * Merge a subclass applies to a patch that shares its row with a concurrent writer. It takes the
   * stored row the patch writes over and the payload, and returns the payload to write. It runs on
   * the same leg as `UnionedReferenceFields`, and on the payload the caller passed — before the
   * union rewrites the declared reference fields. See `PatchResourceOptions.unionReferenceFields`.
   */
  mergeSharedRowPatch?:
    | ((stored: Resource, patch: Record<string, any>) => Record<string, any>)
    | undefined;
  /**
   * Fields a subclass derives from the rest of the resource, such as a searchable tag that mirrors
   * an extension. It runs on every create and on every `patchResource` write, on the resource as it
   * will be stored. A patch reads the stored row first, so the derived fields follow what is stored
   * and not the caller's possibly stale copy.
   */
  deriveOnWrite?: ((resource: Resource) => Resource) | undefined;
}

export type Serialized<Model> =
  Model extends FhirResourceConstructor<infer R>
    ? R
    : Model extends FhirResource<infer R>
      ? R
      : Resource;

/**
 * Reads the current stored copy of a resource to use as a patch base. Direct + cache-busted on
 * purpose: a search (`searchOne`) goes through the FHIR search index, which can lag a
 * just-committed write by hundreds of ms, and the client request cache can hold a pre-edit copy —
 * either would hand back a stale base whose fields the follow-up PUT silently reverts (lost
 * update, ENG-4770). Returns undefined for a missing (404) or deleted (410) resource so the
 * caller can raise the canonical "Resource not found" error instead of leaking a raw outcome.
 */
async function readCurrentResourceForPatch<Model extends FhirResourceConstructor>(
  model: Model & StaticProps<Model>,
  medplum: MedplumClient,
  id: string,
): Promise<Serialized<Model> | undefined> {
  medplum.invalidateUrl(medplum.fhirUrl(model.ResourceType, id));
  try {
    return (await medplum.readResource(model.ResourceType, id)) as Serialized<Model>;
  } catch (error) {
    if (isMissingResourceError(error)) return undefined;
    throw error;
  }
}

/** True for a read that failed because the resource is missing (404) or deleted (410 gone). */
function isMissingResourceError(error: unknown): boolean {
  if (!(error instanceof OperationOutcomeError)) return false;
  const issues = error.outcome?.issue ?? [];
  return issues.some((issue) => issue.code === "not-found" || issue.code === "deleted");
}

/** True for a conditional create the server refused because the criteria match more than one row. */
function isMultipleMatchesError(error: unknown): boolean {
  if (!(error instanceof OperationOutcomeError)) return false;
  const issues = error.outcome?.issue ?? [];
  return issues.some((issue) => issue.code === "multiple-matches");
}

/** The text Medplum puts on a rejected `If-Match`, which it reports as code "processing". */
const PRECONDITION_FAILED_TEXT = preconditionFailed.issue?.[0]?.details?.text;

/**
 * True for a write the stored version moved past. The local `ifMatchVersionId` check raises
 * `conflict`. A server that honors `If-Match` answers with the `preconditionFailed` outcome, which
 * carries code "processing" and puts the reason on `details.text`.
 */
function isVersionConflictError(error: unknown): boolean {
  if (!(error instanceof OperationOutcomeError)) return false;
  const issues = error.outcome?.issue ?? [];
  return issues.some(
    (issue) =>
      issue.code === "conflict" ||
      (issue.code === "processing" && issue.details?.text === PRECONDITION_FAILED_TEXT),
  );
}

/**
 * Adds the stored row's references to the patch payload for each set-valued field. A plain patch
 * replaces an array field, so without this one writer wipes the entries another writer added.
 * Fields the subclass does not declare keep last-write-wins.
 */
function unionMatchedReferenceFields(
  matched: Resource,
  data: Record<string, any>,
  fields: readonly string[] | undefined,
): Record<string, any> {
  if (!fields?.length) return data;

  const matchedFields = matched as unknown as Record<string, unknown>;
  const merged = { ...data };
  for (const field of fields) {
    const matchedRefs = matchedFields[field] as Reference[] | undefined;
    if (!matchedRefs?.length) continue;

    const ourRefs = (data[field] as Reference[] | undefined) ?? [];
    const ourKeys = ourRefs.map((ref) => referenceDedupeKey(ref));
    const missing = matchedRefs.filter((ref) => {
      // A Reference with neither a reference string nor an identifier has no key, so keep it
      // rather than collapse every such entry onto one.
      const key = referenceDedupeKey(ref);
      return key === undefined || !ourKeys.includes(key);
    });
    merged[field] = [...ourRefs, ...missing];
  }

  return merged;
}

/**
 * The value two References must share to count as the same entry. `getReferenceString` returns
 * undefined for a Reference that carries only an `identifier`, so those key on the identifier and
 * dedupe against an equal copy of themselves while staying distinct from other keyless entries.
 */
function referenceDedupeKey(ref: Reference): string | undefined {
  const referenceString = getReferenceString(ref);
  if (referenceString) return referenceString;
  if (ref.identifier) return `identifier:${JSON.stringify(ref.identifier)}`;
  return undefined;
}

/**
 * Prepares a patch that shares its row with a concurrent writer. The subclass merge folds the
 * stored content in first, then the fields the subclass declares as sets take the stored entries
 * as well as the ones in the payload.
 */
function buildSharedRowPatch(
  model: StaticProps,
  stored: Resource,
  patch: Record<string, any>,
): Record<string, any> {
  const merged = model.mergeSharedRowPatch?.(stored, patch) ?? patch;
  return unionMatchedReferenceFields(stored, merged, model.UnionedReferenceFields);
}

/**
 * True when the row a conditional create returned already carries the payload we sent.
 * `createResourceIfNoneExist` hides the HTTP status, so content is the only signal: on a 201 the
 * body is our own payload; on a 200 it is the row a concurrent writer created, which lacks our
 * fields. `meta`, `id`, and `resourceType` are server-owned and never compared. A server that
 * normalizes a field can make this read false for a row we did create; the caller then sends one
 * redundant patch, which is idempotent.
 */
function isCreatePayloadStored(stored: Resource, data: Record<string, any>): boolean {
  const storedFields = stored as unknown as Record<string, unknown>;
  return Object.entries(data).every(([key, value]) => {
    if (key === "id" || key === "meta" || key === "resourceType") return true;
    return deepEquals(value, storedFields[key]);
  });
}

export interface SearchOptions {
  offset?: number;
  limit?: number;
  include?: string;
  sort?: string | string[];
  filter?: string;
}

export interface CreateResourceOptions {
  practiceOrganization?: string | ResourceRef | undefined;
  trustMetaAccountCompartments?: boolean | undefined;
}

export interface CreateResourceIfNoneExistParams<TInput> {
  resource: TInput;
  ifNoneExistQuery: string;
  options?: CreateResourceOptions;
}

export interface UpsertResourceParams<TInput> {
  data: TInput;
  query: Record<string, any>;
  options?: CreateResourceOptions;
}

export interface PatchResourceParams {
  id: string;
  patch: Record<string, any>;
  options?: PatchResourceOptions;
}

export interface PatchResourceOptions {
  allowPracticeCompartmentMutationForMigration?: boolean | undefined;
  /**
   * When set, the write is sent as a conditional update with `If-Match: W/"<versionId>"`.
   * Medplum rejects it with a 412/conflict if the stored resource has moved past this
   * version — turning a blind last-write-wins overwrite into an optimistic-lock failure
   * the caller can surface instead of silently clobbering newer content.
   */
  ifMatchVersionId?: string | undefined;
  /**
   * When set, the patch merges with the stored row instead of replacing it: the reference arrays
   * the model declares in `UnionedReferenceFields` take the stored entries as well, and the model's
   * `mergeSharedRowPatch` runs. `upsertResource` sets it on the leg that patches a row a concurrent
   * writer may share. Unset, every field replaces, so an empty array still clears the key.
   */
  unionReferenceFields?: boolean | undefined;
}

type CreateResourceInput<R extends Resource> = Omit<R, "resourceType"> | ResourceData<R>;

interface NullableIdentifier extends Omit<Identifier, "value"> {
  value?: string | null;
}

interface NullableContactPoint extends Omit<ContactPoint, "value"> {
  value?: string | null;
}

interface NullableCoding extends Omit<Coding, "code"> {
  code?: string | null;
}

export const SubscriptionEventType = {
  Updated: "updated",
  Deleted: "deleted",
} as const;

export interface SubscriptionEventMetadata {
  eventType: ValueOf<typeof SubscriptionEventType>;
}

export type SubscriptionCallback<TResource> = (
  resource: TResource,
  metadata: SubscriptionEventMetadata,
) => any;

export interface ModelSubscription {
  close(): void;
  readyState(): Promise<void>;
  addEventListener?<TEventType extends keyof SubscriptionEventMap>(
    type: TEventType,
    handler: (event: SubscriptionEventMap[TEventType]) => void,
  ): void;
  removeEventListener?<TEventType extends keyof SubscriptionEventMap>(
    type: TEventType,
    handler: (event: SubscriptionEventMap[TEventType]) => void,
  ): void;
}

export interface NullableExtension
  extends Omit<
    Extension,
    | "valueString"
    | "valueBoolean"
    | "valueInteger"
    | "valueDecimal"
    | "valueId"
    | "valueDate"
    | "valueDateTime"
    | "valueTime"
    | "valueCode"
    | "valueCoding"
    | "valueQuantity"
  > {
  valueString?: string | null;
  valueBoolean?: boolean | null;
  valueInteger?: number | null;
  valueDecimal?: number | null;
  valueId?: string | null;
  valueDate?: string | null;
  valueDateTime?: string | null;
  valueTime?: string | null;
  valueCode?: string | null;
  valueCoding?: Coding | null;
  valueQuantity?: Quantity | null;
}

export class FhirResource<T extends Resource> {
  static readonly ResourceType: ResourceType;
  protected readonly resource: T;
  protected client?: MedplumClient;

  public constructor(resource: T) {
    this.resource = resource;
  }

  get id(): string {
    if (!this.resource.id) {
      throw new Error("Missing resource ID");
    }

    return this.resource.id;
  }

  get requiredId(): string {
    return this.id;
  }

  get display(): string | undefined {
    return undefined;
  }

  get referenceString(): string {
    const reference = getReferenceString(this.resource);
    if (!reference) {
      throw new Error("Missing resource reference");
    }

    return reference;
  }

  get referenceObject(): ResourceRef {
    return {
      reference: this.referenceString,
      display: this.display,
    };
  }

  /**
   * @deprecated Use `referenceString` or `referenceObject`.
   */
  get reference(): string {
    return this.referenceString;
  }

  get resourceType(): ResourceType {
    return this.resource.resourceType;
  }

  serialize(): T {
    return this.resource;
  }

  getExtension(url: string): Extension | undefined {
    if (!("extension" in this.resource)) return undefined;
    return this.resource.extension?.find((extension) => extension.url === url);
  }

  getIdentifier(system: string): number | string | undefined {
    if (!("identifier" in this.resource)) return undefined;
    const identifier = findIdentifier(this.resource as ResourceWithIdentifierAndExtension, system);
    return identifier?.value;
  }

  delete(medplum: MedplumClient) {
    const ctor = this.constructor as typeof FhirResource & StaticProps;
    return medplum.deleteResource(ctor.ResourceType, this.id).catch((error) => {
      reportFHIRWriteFailureIfApplicable({
        operation: "deleteResource",
        resourceType: ctor.ResourceType,
        isSyncFrom: ctor.isSyncFrom,
        error,
      });
      throw error;
    });
  }

  // biome-ignore lint/complexity/useMaxParams: Biome counts the `this:` type annotation; runtime params are 3
  static async prepareResourceForCreate<Model extends FhirResourceConstructor = typeof this>(
    this: Model & StaticProps<Model>,
    medplum: MedplumClient,
    resource: CreateResourceInput<Serialized<Model>>,
    options?: CreateResourceOptions,
  ): Promise<Serialized<Model>> {
    const sanitized = sanitizeForCreateValue(resource) as
      | CreateResourceInput<Serialized<Model>>
      | undefined;
    if (!sanitized) {
      throw new Error("Invalid create payload: sanitized to empty");
    }

    // callers may omit resourceType on create - set it first so the hook can branch on it
    const typed = { ...sanitized, resourceType: this.ResourceType } as Resource;
    const derivedResource = this.deriveOnWrite?.(typed) ?? sanitized;
    const derived = derivedResource as CreateResourceInput<Serialized<Model>>;
    const metaForCreate = buildMetaForCreate(derived.meta as MedplumMeta | undefined, options);
    const metaWithAccount = ensurePracticeAccountForCreate({
      resourceType: this.ResourceType,
      resource: derived,
      meta: metaForCreate,
      options,
    });
    const compartments = await getCompartments(medplum, {
      ...derived,
      meta: metaWithAccount,
    } as Serialized<Model>);

    return {
      ...derived,
      resourceType: this.ResourceType,
      meta: {
        ...(metaWithAccount ?? {}),
        lastUpdated: new Date().toISOString(),
        compartment: compartments,
      },
    } as Serialized<Model>;
  }

  protected static async createResource<Model extends FhirResourceConstructor = typeof this>(
    this: Model & StaticProps<Model>,
    medplum: MedplumClient,
    resource: CreateResourceInput<Serialized<Model>>,
    options?: CreateResourceOptions,
  ): Promise<Serialized<Model>>;
  protected static async createResource<R extends Resource>(
    this: StaticProps,
    medplum: MedplumClient,
    resource: CreateResourceInput<R>,
    options?: CreateResourceOptions,
  ): Promise<R>;
  // biome-ignore lint/complexity/useMaxParams: Biome counts the `this:` type annotation; runtime params are 3
  protected static async createResource(
    this: FhirResourceConstructor & StaticProps,
    medplum: MedplumClient,
    resource: CreateResourceInput<Resource>,
    options?: CreateResourceOptions,
  ): Promise<Resource> {
    const preparedResource = await FhirResource.prepareResourceForCreate.call(
      this,
      medplum,
      resource,
      options,
    );
    try {
      return await medplum.createResource<Resource>(preparedResource as Resource);
    } catch (error) {
      reportFHIRWriteFailureIfApplicable({
        operation: "createResource",
        resourceType: this.ResourceType,
        isSyncFrom: this.isSyncFrom,
        error,
      });
      throw error;
    }
  }

  protected static async createResourceIfNoneExist<
    Model extends FhirResourceConstructor = typeof this,
  >(
    this: Model & StaticProps<Model>,
    medplum: MedplumClient,
    params: CreateResourceIfNoneExistParams<CreateResourceInput<Serialized<Model>>>,
  ): Promise<Serialized<Model>>;
  protected static async createResourceIfNoneExist<R extends Resource>(
    this: StaticProps,
    medplum: MedplumClient,
    params: CreateResourceIfNoneExistParams<CreateResourceInput<R>>,
  ): Promise<R>;
  protected static async createResourceIfNoneExist(
    this: FhirResourceConstructor & StaticProps,
    medplum: MedplumClient,
    {
      resource,
      ifNoneExistQuery,
      options,
    }: CreateResourceIfNoneExistParams<CreateResourceInput<Resource>>,
  ): Promise<Resource> {
    const preparedResource = await FhirResource.prepareResourceForCreate.call(
      this,
      medplum,
      resource,
      options,
    );
    try {
      return await medplum.createResourceIfNoneExist<Resource>(
        preparedResource as Resource,
        ifNoneExistQuery,
      );
    } catch (error) {
      reportFHIRWriteFailureIfApplicable({
        operation: "createResourceIfNoneExist",
        resourceType: this.ResourceType,
        isSyncFrom: this.isSyncFrom,
        error,
      });
      throw error;
    }
  }

  protected static async upsertResource<Model extends FhirResourceConstructor = typeof this>(
    this: Model &
      StaticProps<Model> &
      Pick<typeof FhirResource, "patchResource" | "searchOneResource">,
    medplum: MedplumClient,
    { data, query, options }: UpsertResourceParams<CreateResourceInput<Serialized<Model>>>,
  ): Promise<WithId<Serialized<Model>>> {
    // Pin every patch leg to the version it read, so a writer that commits in between raises a
    // version conflict instead of overwriting. A conflict means the row moved, not that the write
    // is invalid, so read the row back by id and patch again: `patchResource` unions the fields the
    // subclass declares in `UnionedReferenceFields` against its own fresh read, and the next attempt
    // merges into the other writer's row. The re-read is direct and cache-busted, because the search
    // index lags the write that just won the race. The last attempt rethrows.
    const patchExisting = async (
      rowId: string,
      rowVersionId: string | undefined,
    ): Promise<WithId<Serialized<Model>>> => {
      let currentId = rowId;
      let currentVersionId = rowVersionId;
      for (let attempt = 1; ; attempt++) {
        try {
          return (await this.patchResource(medplum, {
            id: currentId,
            patch: data,
            options: {
              ifMatchVersionId: currentVersionId,
              unionReferenceFields: true,
            },
          })) as WithId<Serialized<Model>>;
        } catch (error) {
          if (!isVersionConflictError(error) || attempt === UPSERT_PATCH_MAX_ATTEMPTS) throw error;

          // Rethrow when the row is gone, because there is nothing left to merge into.
          const refreshed = await readCurrentResourceForPatch(this, medplum, currentId);
          if (!refreshed?.id) throw error;
          currentId = refreshed.id;
          currentVersionId = refreshed.meta?.versionId;
        }
      }
    };

    const existingResource = await this.searchOneResource(medplum, query);

    if (existingResource?.id) {
      return await patchExisting(existingResource.id, existingResource.meta?.versionId);
    }

    // The search above is a stale read: a concurrent writer can commit between it and this POST.
    // `If-None-Exist` moves the "does it exist?" decision to the server, which either creates the
    // row or returns the one the winner already made. An empty criteria string would match every
    // resource of this type, so fall back to a plain create when the query cleans out to nothing.
    const ifNoneExistQuery = getQueryString(cleanObject(query));
    if (!ifNoneExistQuery) {
      const createdResource = await FhirResource.createResource.call(this, medplum, data, options);
      return createdResource as WithId<Serialized<Model>>;
    }

    let createdResource: Resource | undefined;
    try {
      createdResource = await FhirResource.createResourceIfNoneExist.call(this, medplum, {
        resource: data,
        ifNoneExistQuery,
        options,
      });
    } catch (error) {
      if (!isMultipleMatchesError(error)) throw error;

      // Criteria whose search semantics match more than one row make the server refuse the create.
      // Take the "record exists" leg instead, on the row the search picks. Rethrow when the search
      // finds nothing, because then the refusal has no row behind it.
      const matchedResource = await this.searchOneResource(medplum, query);
      if (!matchedResource?.id) throw error;
      return await patchExisting(matchedResource.id, matchedResource.meta?.versionId);
    }

    // A matched row is the winner's, not ours, and the server dropped this payload. Run the same
    // patch leg the "record exists" path uses so the loser's fields merge in rather than vanish.
    if (createdResource?.id && !isCreatePayloadStored(createdResource, data)) {
      return await patchExisting(createdResource.id, createdResource.meta?.versionId);
    }

    return createdResource as WithId<Serialized<Model>>;
  }

  // biome-ignore lint/complexity/useMaxParams: Biome counts the `this:` type annotation; runtime params are 3
  static async searchResources<Model extends FhirResourceConstructor = typeof this>(
    this: Model & StaticProps<Model>,
    medplum: MedplumClient,
    query: Record<string, any>,
    options?: SearchOptions,
  ): Promise<ResourceArray<Serialized<Model>>> {
    let sort = query._sort ?? options?.sort;

    if (query._sort && options?.sort) {
      sort = Array.isArray(options.sort) ? options.sort.join(",") : options.sort;
    }

    const fullQuery: QueryTypes = {
      ...query,
      _offset: query._offset ?? options?.offset,
      _count: query._count ?? options?.limit,
      _include: query._include ?? options?.include,
      _filter: query._filter ?? options?.filter,
      _sort: sort,
    };

    const cleanQuery = cleanObject(fullQuery);

    if (Object.keys(cleanQuery).length === 0) {
      return createResourceArray<Serialized<Model>>();
    }

    medplum.invalidateSearches(this.ResourceType);
    return (await medplum.searchResources(
      this.ResourceType,
      getQueryString(cleanQuery),
    )) as ResourceArray<Serialized<Model>>;
  }

  // Keep the public default wrappers loose so legacy mobile subclasses can keep bespoke
  // static signatures while shared low-level helpers stay strongly typed.
  static async search(
    medplum: MedplumClient,
    query: Record<string, any>,
    options?: any,
  ): Promise<any> {
    const resources = await FhirResource.searchResources.call(this as any, medplum, query, options);

    return resources.map((resource: any) => {
      const result = new (this as any)(resource);
      result.client = medplum;
      return result;
    });
  }

  static async searchOne(
    medplum: MedplumClient,
    query: Record<string, any>,
    options?: any,
  ): Promise<any> {
    const resource = await FhirResource.searchOneResource.call(
      this as any,
      medplum,
      query,
      options,
    );
    if (!resource) {
      return null;
    }

    const result = new (this as any)(resource);
    result.client = medplum;
    return result;
  }

  static async getById(medplum: MedplumClient, id: string): Promise<any> {
    try {
      const resource = await medplum.readResource(this.ResourceType, id);
      const result = new (this as any)(resource);
      result.client = medplum;
      return result;
    } catch (error) {
      if (
        error instanceof OperationOutcomeError &&
        error.outcome?.issue?.some((issue) => issue.code === "not-found")
      ) {
        return undefined;
      }

      throw error;
    }
  }

  protected static extractSearchResources<TResource extends Resource>(
    result: unknown,
  ): TResource[] {
    if (!result) return [];

    if (Array.isArray(result)) {
      return result.filter((resource): resource is TResource => {
        return Boolean(resource && typeof resource === "object" && "resourceType" in resource);
      });
    }

    if (isPlainObject(result) && result.resourceType === "Bundle" && Array.isArray(result.entry)) {
      return result.entry
        .map((entry) => (isPlainObject(entry) ? entry.resource : undefined))
        .filter((resource): resource is TResource => {
          return Boolean(resource && typeof resource === "object" && "resourceType" in resource);
        });
    }

    if (
      isPlainObject(result) &&
      isPlainObject(result.bundle) &&
      Array.isArray(result.bundle.entry)
    ) {
      return result.bundle.entry
        .map((entry) => (isPlainObject(entry) ? entry.resource : undefined))
        .filter((resource): resource is TResource => {
          return Boolean(resource && typeof resource === "object" && "resourceType" in resource);
        });
    }

    return [];
  }

  static async executeBatch(
    medplum: MedplumClient,
    bundle: Bundle,
    options?: Parameters<MedplumClient["executeBatch"]>[1],
  ): Promise<Bundle> {
    return await medplum.executeBatch(bundle, options);
  }

  static async getCount(
    medplum: MedplumClient,
    query: Record<string, any>,
    options?: SearchOptions,
  ): Promise<number> {
    const cleanQuery = cleanObject({
      ...query,
      _filter: query._filter ?? options?.filter,
    });
    if (Object.keys(cleanQuery).length === 0) {
      return 0;
    }

    const bundle = await medplum.search(this.ResourceType, {
      ...cleanQuery,
      _count: 1,
      _total: "accurate",
    });

    return bundle.total ?? 0;
  }

  static subscribeToResource(
    medplum: MedplumClient,
    query: QueryTypes,
    callback: SubscriptionCallback<any>,
  ): ModelSubscription {
    const resourceClass = this;
    const criteria = getQueryString(query);
    if (!criteria) {
      throw new Error("No criteria provided");
    }

    const criteriaString = `${resourceClass.ResourceType}?${criteria}`;
    const subscription = medplum.subscribeToCriteria(criteriaString);

    function handleMessage(event: { type: "message"; payload: Bundle<Resource> }) {
      const entries = event.payload?.entry ?? [];

      const matchingResource = entries.find(
        (entry) => entry.resource?.resourceType === resourceClass.ResourceType,
      )?.resource;

      if (matchingResource) {
        callback(matchingResource, { eventType: SubscriptionEventType.Updated });
        return;
      }

      const subscriptionStatus = entries.find(
        (entry) => entry.resource?.resourceType === "SubscriptionStatus",
      )?.resource as { notificationEvent?: Array<{ focus?: { reference?: string } }> } | undefined;

      const focusRef = subscriptionStatus?.notificationEvent?.[0]?.focus?.reference;
      if (focusRef) {
        const deletedId = focusRef.split("/").pop();
        if (deletedId) {
          const minimalResource = {
            resourceType: resourceClass.ResourceType,
            id: deletedId,
          } as Resource;
          callback(minimalResource, { eventType: SubscriptionEventType.Deleted });
        }
      }
    }

    subscription.addEventListener("message", handleMessage);

    return {
      close() {
        subscription.removeEventListener("message", handleMessage);
        medplum.unsubscribeFromCriteria(criteriaString);
      },
      addEventListener: subscription.addEventListener.bind(subscription),
      removeEventListener: subscription.removeEventListener.bind(subscription),
      readyState() {
        return new Promise<void>((resolve) => {
          subscription.addEventListener("open", (_event: SubscriptionEventMap["open"]) => {
            resolve();
          });
        });
      },
    };
  }

  // biome-ignore lint/complexity/useMaxParams: Biome counts the `this:` type annotation; runtime params are 3
  static async searchOneResource<Model extends FhirResourceConstructor = typeof this>(
    this: Model & StaticProps<Model>,
    medplum: MedplumClient,
    query: Record<string, any>,
    options?: SearchOptions,
  ): Promise<Serialized<Model> | undefined> {
    let sort = query._sort ?? options?.sort;

    if (query._sort && options?.sort) {
      sort = Array.isArray(options.sort) ? options.sort.join(",") : options.sort;
    }

    const fullQuery: QueryTypes = {
      ...query,
      _offset: query._offset ?? options?.offset,
      _count: query._count ?? options?.limit,
      _include: query._include ?? options?.include,
      _filter: query._filter ?? options?.filter,
      _sort: sort,
    };

    const cleanQuery = cleanObject(fullQuery);

    if (Object.keys(cleanQuery).length === 0) {
      return undefined;
    }

    medplum.invalidateSearches(this.ResourceType);
    return (await medplum.searchOne(this.ResourceType, cleanQuery)) as Serialized<Model>;
  }

  static async delete<Model extends FhirResourceConstructor = typeof this>(
    this: Model & StaticProps<Model>,
    medplum: MedplumClient,
    id: string,
  ) {
    try {
      await medplum.deleteResource(this.ResourceType, id);
    } catch (error) {
      reportFHIRWriteFailureIfApplicable({
        operation: "deleteResource",
        resourceType: this.ResourceType,
        isSyncFrom: this.isSyncFrom,
        error,
      });
      throw error;
    }
  }

  static async patchResource<Model extends FhirResourceConstructor = typeof this>(
    this: Model & StaticProps<Model>,
    medplum: MedplumClient,
    { id, patch, options }: PatchResourceParams,
  ): Promise<Serialized<Model>> {
    try {
      medplum.invalidateSearches(this.ResourceType);
      // ALL patch base reads are direct + uncached, never via `searchOne`: the search index lags
      // a just-committed write, so an index-based base would be missing whatever a concurrent
      // writer just added and the PUT below would silently revert it (lost update, ENG-4770).
      // The same fresh read feeds the optimistic-concurrency check for ifMatch writes.
      const medplumResource = await readCurrentResourceForPatch(this, medplum, id);
      if (!medplumResource) {
        throw new Error(`Resource ${this.ResourceType}/${id} not found`);
      }

      // Reject here when the stored version moved past the base the caller pinned. Deterministic and
      // independent of server-side `If-Match` enforcement (local/self-hosted stacks don't always
      // honor it); the header below still guards the read→write race on the server. conflict() yields
      // issue.code "conflict", which isMedplumVersionConflictError classifies as a version conflict.
      const storedVersionId = medplumResource.meta?.versionId;
      if (
        options?.ifMatchVersionId &&
        storedVersionId &&
        storedVersionId !== options.ifMatchVersionId
      ) {
        throw new OperationOutcomeError(
          conflict(
            `Version conflict on ${this.ResourceType}/${id}: stored ${storedVersionId}, expected ${options.ifMatchVersionId}`,
          ),
        );
      }

      const patchData = options?.unionReferenceFields
        ? buildSharedRowPatch(this, medplumResource as Resource, patch)
        : patch;
      const patched = patchResource(medplumResource as Resource, patchData, options);
      const patchedResource = this.deriveOnWrite?.(patched) ?? patched;
      const updateOptions = options?.ifMatchVersionId
        ? { headers: { "If-Match": weakETag(options.ifMatchVersionId) } }
        : undefined;
      return await medplum.updateResource<Serialized<Model>>(
        patchedResource as Serialized<Model>,
        updateOptions,
      );
    } catch (error) {
      reportFHIRWriteFailureIfApplicable({
        operation: "patchResource",
        resourceType: this.ResourceType,
        isSyncFrom: this.isSyncFrom,
        error,
      });
      throw error;
    }
  }

  static async patch<TResource extends Resource>(
    medplum: MedplumClient,
    params: PatchResourceParams,
  ): Promise<TResource> {
    return (await (this as typeof FhirResource).patchResource(
      medplum as MedplumClient,
      params,
    )) as TResource;
  }

  // biome-ignore-start lint/complexity/useMaxParams: Biome counts the `this:` type annotation; runtime params are 3
  protected static async patchResourceWithJsonPatch<
    Model extends FhirResourceConstructor = typeof this,
  >(
    this: Model & StaticProps<Model>,
    medplum: MedplumClient,
    id: string,
    operations: PatchOperation[],
  ): Promise<Serialized<Model>> {
    // biome-ignore-end lint/complexity/useMaxParams: Biome counts the `this:` type annotation; runtime params are 3
    try {
      medplum.invalidateSearches(this.ResourceType);
      return (await medplum.patchResource(this.ResourceType, id, operations)) as Serialized<Model>;
    } catch (error) {
      reportFHIRWriteFailureIfApplicable({
        operation: "patchResourceWithJsonPatch",
        resourceType: this.ResourceType,
        isSyncFrom: this.isSyncFrom,
        error,
      });
      throw error;
    }
  }
}

interface ResourceCompartmentSource {
  meta?: MedplumMeta | undefined;
  subject?: unknown;
  patient?: unknown;
}

/**
 * Extracts a non-empty `reference` string from an unknown Reference-shaped value.
 *
 * Some FHIR fields this is used against (e.g. `Contract.subject`) are typed as
 * `Reference[]`, not a single `Reference` — an array falls through the plain-object
 * check otherwise, silently dropping compartment tagging for those resources even
 * though a usable reference is right there (ENG-4663 follow-up). Takes the first
 * array entry with a non-empty reference, matching the single-value behavior below
 * (no type filtering here — same as the non-array case).
 */
function referenceStringFromUnknown(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const reference = referenceStringFromUnknown(entry);
      if (reference) return reference;
    }
    return undefined;
  }
  if (!isPlainObject(value)) return undefined;
  const reference = value.reference;
  return typeof reference === "string" && reference.length > 0 ? reference : undefined;
}

/** Extracts an `Organization/...` reference (with optional display) from an unknown value. */
function organizationRefFromUnknown(value: unknown): ResourceRef | undefined {
  const reference = referenceStringFromUnknown(value);
  if (!reference?.startsWith("Organization/")) return undefined;
  const display =
    isPlainObject(value) && typeof value.display === "string" ? value.display : undefined;
  return { reference, display };
}

/** The `Patient/...` reference a resource is scoped to, from its `subject`/`patient` field. */
function patientReferenceFromResource(resource: ResourceCompartmentSource): string | undefined {
  const reference =
    referenceStringFromUnknown(resource.subject) ?? referenceStringFromUnknown(resource.patient);
  return reference?.startsWith("Patient/") ? reference : undefined;
}

type ResourceWithPracticeLinks = ResourceCompartmentSource & { managingOrganization?: unknown };

/**
 * Derives the practice Organization for a create when the caller didn't provide one.
 *
 * Intentionally I/O-free: we only self-heal from data already on the payload — a Patient's own
 * `managingOrganization`. We deliberately do NOT read the referenced Patient to inherit its
 * account for other resource types: a read on every practice-scoped create would change create
 * semantics and add hot-path cost (and several models assert that `create` performs no reads).
 * `hadLinkage` distinguishes "practice-scoped but unresolved" (a candidate leak worth reporting)
 * from "no practice linkage at all" (legitimately project/super-org scoped — stay silent).
 */
function derivePracticeOrganizationForCreate(
  resourceType: ResourceType,
  resource: ResourceWithPracticeLinks,
): { ref: ResourceRef | undefined; hadLinkage: boolean } {
  if (resourceType === "Patient") {
    return {
      ref: organizationRefFromUnknown(resource.managingOrganization),
      hadLinkage: resource.managingOrganization !== undefined,
    };
  }

  // Other patient-scoped resources: observe the gap, don't auto-heal (no read).
  return { ref: undefined, hadLinkage: patientReferenceFromResource(resource) !== undefined };
}

/**
 * Self-heal: when a create reaches the write path with no resolved practice account (no
 * `practiceOrganization` option and no trusted `meta.accounts`), derive the account from data
 * already on the payload so it lands in the practice compartment instead of being silently
 * created project-wide (invisible to practice-scoped reads). Emits telemetry so leaky callers —
 * which should pass `options.practiceOrganization` — can be found and fixed. Never throws, never
 * performs I/O.
 */
function ensurePracticeAccountForCreate({
  resourceType,
  resource,
  meta,
  options,
}: {
  resourceType: ResourceType;
  resource: ResourceWithPracticeLinks;
  meta: MedplumMeta | undefined;
  options: CreateResourceOptions | undefined;
}): MedplumMeta | undefined {
  // Explicit practice context or trusted meta already resolved the account — nothing to heal.
  if (options?.practiceOrganization !== undefined) return meta;
  if (getPracticeOrganizationFromMeta(meta)) return meta;

  const { ref, hadLinkage } = derivePracticeOrganizationForCreate(resourceType, resource);

  if (ref) {
    reportAccountCompartmentFallback({
      resourceType,
      outcome: "self-healed",
      practiceReference: ref.reference,
    });
    return { ...(meta ?? {}), accounts: [ref] };
  }

  if (hadLinkage) {
    reportAccountCompartmentFallback({ resourceType, outcome: "unresolved" });
  }
  return meta;
}

export async function getCompartments(medplum: MedplumClient, resource: ResourceCompartmentSource) {
  const compartmentsByReference = new Map<string, ResourceRef>();

  function addCompartment(candidate: ResourceRef) {
    upsertReferenceByReference(compartmentsByReference, candidate);
  }

  const superOrganizationId = process.env.VIBRANT_SUPER_ORGANIZATION_ID;
  if (superOrganizationId) {
    addCompartment({
      reference: `Organization/${superOrganizationId}`,
      display: "Vibrant Super-Organization",
    });
  }

  const patientRef =
    referenceStringFromUnknown(resource.subject) ?? referenceStringFromUnknown(resource.patient);
  if (typeof patientRef === "string" && patientRef.length > 0) {
    addCompartment({
      display: "Patient",
      reference: patientRef,
    });
  } else {
    const profile = await medplum.getProfileAsync();
    if (
      profile?.resourceType === "Patient" &&
      typeof profile.id === "string" &&
      profile.id.length > 0
    ) {
      addCompartment({
        display: "Patient",
        reference: `Patient/${profile.id}`,
      });
    }
  }

  const practiceOrganization = getPracticeOrganizationFromMeta(resource.meta);
  if (practiceOrganization) {
    addCompartment({
      reference: practiceOrganization.reference,
      display: practiceOrganization.display ?? "Organization",
    });
  }

  return Array.from(compartmentsByReference.values()) satisfies Reference[];
}

function buildMetaForCreate(
  meta: MedplumMeta | undefined,
  options: CreateResourceOptions | undefined,
): MedplumMeta | undefined {
  const hasCallerProvidedAccountMeta = meta?.accounts !== undefined || meta?.account !== undefined;
  if (hasCallerProvidedAccountMeta) {
    const allowUnsafe = options?.trustMetaAccountCompartments === true;
    const hasExplicitPractice = options?.practiceOrganization !== undefined;

    if (!allowUnsafe || hasExplicitPractice) {
      const forbiddenKeys = [
        meta?.accounts !== undefined ? "meta.accounts" : undefined,
        meta?.account !== undefined ? "meta.account" : undefined,
      ].filter((key): key is string => typeof key === "string");

      throw new Error(
        `Unsafe create payload: ${forbiddenKeys.join(
          " and ",
        )} must not be set directly. Pass practice context via create options (practiceOrganization).`,
      );
    }
  }

  const practiceOrganization = normalizePracticeOrganizationOption(options?.practiceOrganization);
  if (practiceOrganization) {
    const { account: _account, accounts: _accounts, ...rest } = meta ?? {};
    return { ...rest, accounts: [practiceOrganization] };
  }

  if (options?.trustMetaAccountCompartments === true) {
    assertValidAccountCompartments({ meta });
    return meta;
  }

  if (!meta) return undefined;
  const { account: _account, accounts: _accounts, ...rest } = meta;
  return rest;
}

function normalizePracticeOrganizationOption(
  practiceOrganization: CreateResourceOptions["practiceOrganization"],
): ResourceRef | undefined {
  if (practiceOrganization === undefined) return undefined;

  const reference =
    typeof practiceOrganization === "string"
      ? practiceOrganization
      : practiceOrganization.reference;

  const display =
    typeof practiceOrganization === "string" ? undefined : practiceOrganization.display;

  if (typeof reference !== "string" || reference.length === 0) {
    throw new Error(
      "Invalid practiceOrganization option: expected a non-empty reference string (e.g. Organization/<id>).",
    );
  }

  if (!reference.startsWith("Organization/")) {
    throw new Error(
      `Invalid practiceOrganization option: expected an Organization reference, got "${reference}".`,
    );
  }

  if (display !== undefined && typeof display !== "string") {
    throw new Error("Invalid practiceOrganization option: display must be a string if provided.");
  }

  return { reference, display };
}

type ResourceWithIdentifiers = Resource & { identifier: Identifier[] };
type ResourceWithTelecom = Resource & { telecom: ContactPoint[] };
type ResourceWithExtensions = Resource & { extension: Extension[] };
type ResourceWithMeta = Resource & { meta: Meta };

export function patchResource<T extends Resource>(
  resource: T,
  patch: Partial<T>,
  options?: PatchResourceOptions,
) {
  for (const key in patch) {
    const newValue = patch[key];
    const oldValue = resource[key];

    if (newValue === undefined) continue;
    if (key === "resourceType") {
      continue;
    }

    if (key === "identifier") {
      const resourceWithIdentifiers = resource as ResourceWithIdentifiers;
      resourceWithIdentifiers.identifier = patchIdentifiers(
        oldValue as Identifier[],
        newValue as NullableIdentifier[],
      );
      continue;
    }

    if (key === "telecom") {
      const resourceWithTelecom = resource as ResourceWithTelecom;
      resourceWithTelecom.telecom = patchTelecom(
        oldValue as ContactPoint[],
        newValue as NullableContactPoint[],
      );
      continue;
    }

    if (key === "extension") {
      const resourceWithExtensions = resource as ResourceWithExtensions;
      resourceWithExtensions.extension = patchExtensions(
        oldValue as Extension[],
        newValue as NullableExtension[],
      );
      continue;
    }

    if (key === "meta") {
      assertSafeMetaPatch(newValue, options);
      const resourceWithMeta = resource as ResourceWithMeta;
      resourceWithMeta.meta = patchMeta(oldValue as Meta, newValue as Partial<NullableProps<Meta>>);
      continue;
    }

    if (key === "type") {
      const maybeCodeableConcept = newValue as unknown;
      if (isCodeableConceptPatch(maybeCodeableConcept)) {
        const patched = patchCodeableConcept(
          oldValue as CodeableConcept | undefined,
          (maybeCodeableConcept.coding ?? []) as NullableCoding[],
        );

        if (!patched) {
          delete (resource as any)[key];
        } else {
          (resource as any)[key] = patched;
        }
        continue;
      }
    }

    if (
      newValue === null ||
      newValue === "" ||
      (Array.isArray(newValue) && newValue.length === 0)
    ) {
      delete resource[key];
      continue;
    }

    resource[key as keyof T] = newValue as T[keyof T];
  }

  return resource;
}

function assertSafeMetaPatch(value: unknown, options: PatchResourceOptions | undefined): void {
  if (options?.allowPracticeCompartmentMutationForMigration === true) return;
  if (!isPlainObject(value)) return;

  const forbiddenKeys = ["accounts", "account", "compartment"].filter((key) =>
    Object.hasOwn(value, key),
  );
  if (forbiddenKeys.length === 0) return;

  const forbiddenPathList = forbiddenKeys.map((key) => `meta.${key}`).join(" and ");
  throw new Error(
    `Unsafe patch payload: ${forbiddenPathList} must not be set directly. Practice compartments are immutable; use an explicit migration/break-glass path.`,
  );
}

function isCodeableConceptPatch(value: unknown): value is Partial<CodeableConcept> {
  const codeableConceptPatchSchema = z.object({
    coding: z.array(z.object({}).passthrough()),
  });

  return codeableConceptPatchSchema.safeParse(value).success;
}

function patchIdentifiers(
  existingIdentifiers: Identifier[] = [],
  identifiersPatch: NullableIdentifier[] = [],
) {
  const identifierMap: Record<string, Identifier> = {};

  for (const identifier of existingIdentifiers) {
    if (!identifier.system) continue;
    identifierMap[identifier.system] = identifier;
  }

  for (const identifier of identifiersPatch) {
    if (!identifier.system) continue;
    if (identifier.value === undefined) continue;

    if (identifier.value === null) {
      delete identifierMap[identifier.system];
      continue;
    }

    identifierMap[identifier.system] = identifier as Identifier;
  }

  const patchedIdentifiers: Identifier[] = [];
  const emittedSystems = new Set<string>();
  const patchedSystems = new Set(
    identifiersPatch
      .filter((identifier) => identifier.system && identifier.value !== undefined)
      .map((identifier) => identifier.system),
  );

  for (const identifier of existingIdentifiers) {
    // a system this patch does not name stays as stored - including repeats, such as one
    // Fullscript patient id per Fullscript account
    if (!identifier.system || !patchedSystems.has(identifier.system)) {
      patchedIdentifiers.push(identifier);
      continue;
    }

    const patchedIdentifier = identifierMap[identifier.system];
    if (!patchedIdentifier || emittedSystems.has(identifier.system)) {
      continue;
    }

    patchedIdentifiers.push(patchedIdentifier);
    emittedSystems.add(identifier.system);
  }

  for (const identifier of identifiersPatch) {
    if (
      !identifier.system ||
      emittedSystems.has(identifier.system) ||
      !patchedSystems.has(identifier.system)
    ) {
      continue;
    }

    const patchedIdentifier = identifierMap[identifier.system];
    if (!patchedIdentifier) {
      continue;
    }

    patchedIdentifiers.push(patchedIdentifier);
    emittedSystems.add(identifier.system);
  }

  return patchedIdentifiers;
}

function patchTelecom(
  existingTelecom: ContactPoint[] = [],
  telecomPatch: NullableContactPoint[] = [],
): ContactPoint[] {
  const patchedTelecom = existingTelecom.map((telecom) => ({ ...telecom }));

  for (const patch of telecomPatch) {
    if (!patch.system || patch.value === undefined) continue;

    const telecomIndex = findTelecomPatchIndex({
      telecomEntries: patchedTelecom,
      patch,
    });

    if (patch.value === null) {
      if (telecomIndex >= 0) {
        patchedTelecom.splice(telecomIndex, 1);
      }
      continue;
    }

    if (telecomIndex >= 0) {
      patchedTelecom[telecomIndex] = {
        ...patchedTelecom[telecomIndex],
        ...cleanObject(patch),
      };
      continue;
    }

    patchedTelecom.push(cleanObject(patch) as ContactPoint);
  }

  return patchedTelecom;
}

function findTelecomPatchIndex({
  telecomEntries,
  patch,
}: {
  telecomEntries: ContactPoint[];
  patch: NullableContactPoint;
}): number {
  const exactMatchIndex = telecomEntries.findIndex((telecom) => {
    if (telecom.system !== patch.system) {
      return false;
    }

    if (patch.use !== undefined && telecom.use !== patch.use) {
      return false;
    }

    if (patch.rank !== undefined && telecom.rank !== patch.rank) {
      return false;
    }

    return true;
  });

  if (exactMatchIndex >= 0) {
    return exactMatchIndex;
  }

  return telecomEntries.findIndex((telecom) => telecom.system === patch.system);
}

export function patchCodeableConcept(
  existing: CodeableConcept | undefined,
  codingsPatch: NullableCoding[],
): CodeableConcept | undefined {
  const existingCodings = existing?.coding ?? [];

  const codingMap: Record<string, Coding> = {};
  const nonSystemCodings: Coding[] = [];

  for (const coding of existingCodings) {
    if (!coding.system) {
      nonSystemCodings.push(coding);
      continue;
    }
    codingMap[coding.system] = coding;
  }

  for (const patch of codingsPatch ?? []) {
    const system = patch.system;
    if (!system) continue;
    if (patch.code === undefined) continue;

    if (patch.code === null) {
      delete codingMap[system];
      continue;
    }

    codingMap[system] = {
      ...(codingMap[system] ?? {}),
      ...patch,
      system,
    } as Coding;
  }

  const resultCodings = [...nonSystemCodings, ...Object.values(codingMap)];

  if (resultCodings.length === 0) return undefined;
  return existing ? { ...existing, coding: resultCodings } : { coding: resultCodings };
}

/**
 * Merges an extension patch by URL. Only URLs named in the patch change: each one collapses to a
 * single merged entry at its first position. Every other extension stays as it was, repeats
 * included, so a multi-valued extension (one entry per tag) survives an unrelated patch.
 */
function patchExtensions(
  existingExtensions: Extension[] | undefined,
  extensionsPatch: NullableExtension[],
) {
  const patchedUrls = new Set((extensionsPatch ?? []).map((data) => data.url));
  const extensionMap: Record<string, Extension> = {};
  // patched URLs hold a placeholder slot (their url) so they keep their place; the rest pass through
  const slots: (Extension | string)[] = [];

  for (const extension of existingExtensions ?? []) {
    if (!patchedUrls.has(extension.url)) {
      slots.push(extension);
      continue;
    }
    if (extensionMap[extension.url]) continue;
    extensionMap[extension.url] = { ...extension };
    slots.push(extension.url);
  }

  for (const data of extensionsPatch ?? []) {
    if (!extensionMap[data.url]) {
      extensionMap[data.url] = { url: data.url };
      slots.push(data.url);
    }

    for (const key in data) {
      if (key === "url") continue;

      if (data[key as keyof Extension] === undefined) continue;

      if (data[key as keyof Extension] === null) {
        delete extensionMap[data.url][key as keyof Extension];
        continue;
      }

      (extensionMap[data.url] as any)[key] = data[key as keyof Extension];
    }
  }

  // an entry left with only its url was fully nulled - drop it
  return slots.flatMap((slot) => {
    if (typeof slot !== "string") return [slot];
    const merged = extensionMap[slot];
    return Object.keys(merged).length > 1 ? [merged] : [];
  });
}

function patchMeta(oldValue: Meta | undefined, newValue: Partial<NullableProps<Meta>>) {
  const meta: Record<string, any> = { ...oldValue };

  for (const key in newValue) {
    const value = newValue[key as keyof Meta];

    if (value === null) {
      delete meta[key as keyof Meta];
      continue;
    }

    if (value !== undefined) {
      meta[key as keyof Meta] = value;
    }
  }

  return meta;
}
